import httpService from "./httpService";

export type UserActivityPeriod = "hourly" | "daily";

export interface UserActivityBucket {
    datetime: string;
    counts: {
        web: number;
        ios: number;
        android: number;
        chatgpt: number;
        claude: number;
        gemini: number;
        other_mcp: number;
        all: number;
    };
}

export interface UserActivityReport {
    period: UserActivityPeriod;
    timezone: "UTC";
    items: UserActivityBucket[];
}

export const USER_ACTIVITY_DEFAULT_DAYS = 30;
export const USER_ACTIVITY_MAX_DAYS = 365;

const ACTIVITY_COUNT_FIELDS = ["web", "ios", "android", "chatgpt", "claude", "gemini", "other_mcp", "all"] as const;

function isRecord(candidate: unknown): candidate is Record<string, unknown> {
    return typeof candidate === "object" && candidate !== null && !Array.isArray(candidate);
}

class AdminUserActivityService {
    private async getReport(
        period: UserActivityPeriod,
        windowSize: number,
        signal?: AbortSignal
    ): Promise<UserActivityReport> {
        const dailyQuery = period === "daily" ? `?days=${windowSize}` : "";
        const activityResponse = await httpService.get<unknown>(
            `/v3/admin/users/activity/${period}${dailyQuery}`,
            { signal, requestScope: "actor", requiresAuth: true, cache: "no-store" }
        );
        if (!this.isActivityReport(activityResponse.data, period, windowSize)) {
            throw new Error("Invalid user activity report");
        }
        return activityResponse.data;
    }

    private isActivityReport(activityReport: unknown, period: UserActivityPeriod, windowSize: number): activityReport is UserActivityReport {
        if (!isRecord(activityReport) || activityReport.period !== period || activityReport.timezone !== "UTC"
            || !Array.isArray(activityReport.items) || activityReport.items.length > windowSize) {
            return false;
        }
        const bucketIntervalMs = (period === "hourly" ? 1 : 24) * 60 * 60 * 1000;
        let previousBucketTimestamp = -Infinity;
        for (const activityBucket of activityReport.items) {
            if (!isRecord(activityBucket) || !this.isActivityTimestamp(activityBucket.datetime, bucketIntervalMs)
                || !this.isActivityCounts(activityBucket.counts)) {
                return false;
            }
            const bucketTimestamp = Date.parse(activityBucket.datetime);
            if (bucketTimestamp <= previousBucketTimestamp) {
                return false;
            }
            previousBucketTimestamp = bucketTimestamp;
        }
        return true;
    }

    private isActivityTimestamp(datetime: unknown, bucketIntervalMs: number): datetime is string {
        if (typeof datetime !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.0+)?Z$/.test(datetime)) {
            return false;
        }
        const bucketTimestamp = Date.parse(datetime);
        if (!Number.isFinite(bucketTimestamp) || bucketTimestamp % bucketIntervalMs !== 0) {
            return false;
        }
        // Date.parse normalizes impossible dates such as February 30; reject that rollover.
        return new Date(bucketTimestamp).toISOString().slice(0, 19) === datetime.slice(0, 19);
    }

    private isActivityCounts(activityCounts: unknown): activityCounts is UserActivityBucket["counts"] {
        if (!isRecord(activityCounts)) {
            return false;
        }
        const allClientCount = activityCounts.all;
        if (typeof allClientCount !== "number" || !Number.isSafeInteger(allClientCount) || allClientCount < 0) {
            return false;
        }
        return ACTIVITY_COUNT_FIELDS.every(countField => {
            const clientCount = activityCounts[countField];
            return typeof clientCount === "number" && Number.isSafeInteger(clientCount)
                && clientCount >= 0 && clientCount <= allClientCount;
        });
    }

    public getHourly(signal?: AbortSignal): Promise<UserActivityReport> {
        return this.getReport("hourly", 24, signal);
    }

    public async getDaily(days = USER_ACTIVITY_DEFAULT_DAYS, signal?: AbortSignal): Promise<UserActivityReport> {
        if (!Number.isInteger(days) || days < 1 || days > USER_ACTIVITY_MAX_DAYS) {
            throw new Error("Days must be a whole number from 1 to 365");
        }
        return this.getReport("daily", days, signal);
    }
}

const adminUserActivityService = new AdminUserActivityService();
export default adminUserActivityService;
